import React, { useState } from 'react';
import {
  Share2,
  Globe,
  Code,
  Building2,
  UserCheck,
  CheckCircle2,
  BookOpen,
  ArrowRight,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';

interface ShowcaseLandingSectionProps {
  onOpenRegister?: () => void;
}

export const ShowcaseLandingSection: React.FC<ShowcaseLandingSectionProps> = ({
  onOpenRegister,
}) => {
  const [activeTab, setActiveTab] = useState<'penerbit' | 'penulis'>('penerbit');

  return (
    <section className="w-full max-w-7xl 2xl:max-w-[1536px] 3xl:max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-10 my-12 2xl:my-16">
      <div className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 text-white rounded-3xl p-6 sm:p-10 2xl:p-12 shadow-2xl border border-indigo-900/60">
        {/* Decorative background grid effect */}
        <div className="absolute inset-0 bg-[radial-gradient(#818cf8_1px,transparent_1px)] [background-size:28px_28px] opacity-10 pointer-events-none" />

        <div className="relative z-10 space-y-8 2xl:space-y-10">
          {/* Header Title & Badge */}
          <div className="text-center max-w-3xl 2xl:max-w-4xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-xs sm:text-sm 2xl:text-base font-bold uppercase tracking-wider">
              <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5 text-indigo-400" />
              <span>Fitur Halaman Publik UIBN</span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl 2xl:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Halaman Publik Resmi & Portofolio Digital Karya
            </h2>

            <p className="text-sm sm:text-base md:text-lg 2xl:text-xl text-slate-300 leading-relaxed">
              Halaman publik profesional untuk menampilkan seluruh katalog terbitan terverifikasi, e-sertifikat, serta tautan promosi karya.
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="flex items-center justify-center gap-2.5">
            <button
              type="button"
              onClick={() => setActiveTab('penerbit')}
              className={`px-4 sm:px-5 2xl:px-6 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm 2xl:text-base font-bold transition flex items-center gap-2 cursor-pointer ${
                activeTab === 'penerbit'
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30 border border-indigo-400/40'
                  : 'bg-slate-800/80 text-slate-300 hover:bg-slate-800 border border-slate-700'
              }`}
            >
              <Building2 className="w-4 h-4 2xl:w-5 2xl:h-5 text-indigo-300" />
              <span>Format Penerbit</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('penulis')}
              className={`px-4 sm:px-5 2xl:px-6 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm 2xl:text-base font-bold transition flex items-center gap-2 cursor-pointer ${
                activeTab === 'penulis'
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30 border border-indigo-400/40'
                  : 'bg-slate-800/80 text-slate-300 hover:bg-slate-800 border border-slate-700'
              }`}
            >
              <UserCheck className="w-4 h-4 2xl:w-5 2xl:h-5 text-indigo-300" />
              <span>Format Penulis</span>
            </button>
          </div>

          {/* Split Grid: Clean Visual Preview & Core Benefits */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 2xl:gap-10 items-center">
            {/* Left: Clean Visual Mockup Preview */}
            <div className="lg:col-span-6 bg-slate-900/90 border border-slate-800 rounded-2xl p-5 2xl:p-6 shadow-xl space-y-4 relative overflow-hidden">
              {/* Mockup Top Address Bar */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                </div>

                <div className="px-3 py-1 rounded-lg bg-slate-800 text-xs sm:text-sm font-mono text-indigo-300 border border-slate-700 flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 text-indigo-400 shrink-0" />
                  <span>
                    uibn.id/{activeTab === 'penerbit' ? 'penerbit/katalog-resmi' : 'penulis/katalog-karya'}
                  </span>
                </div>
              </div>

              {/* Mockup Profile Header */}
              <div className="flex items-center justify-between gap-3 pt-1">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 2xl:w-12 2xl:h-12 rounded-xl bg-indigo-600/40 border border-indigo-400/40 flex items-center justify-center text-indigo-300 font-bold">
                    {activeTab === 'penerbit' ? <Building2 className="w-5 h-5 2xl:w-6 2xl:h-6" /> : <UserCheck className="w-5 h-5 2xl:w-6 2xl:h-6" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <h4 className="font-bold text-xs sm:text-sm 2xl:text-base text-white">
                        {activeTab === 'penerbit' ? 'Katalog Lembaga Penerbit' : 'Katalog Penulis / Akademisi'}
                      </h4>
                      <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400 shrink-0" />
                    </div>
                    <p className="text-xs sm:text-sm text-slate-400 font-mono">
                      {activeTab === 'penerbit' ? 'Kode Penerbit Resmi UIBN' : 'Penulis Terdaftar UIBN'}
                    </p>
                  </div>
                </div>

                <div className="px-2.5 py-1 rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-xs sm:text-sm font-bold">
                  Terverifikasi
                </div>
              </div>

              {/* Sample Catalog Items */}
              <div className="space-y-2 pt-1">
                <span className="text-xs sm:text-sm uppercase font-bold text-slate-400 tracking-wider block">
                  Pratinjau Katalog Terbitan:
                </span>

                <div className="grid grid-cols-2 gap-2.5">
                  <div className="p-3 rounded-xl bg-slate-800/80 border border-slate-700/80 flex items-center gap-2.5">
                    <div className="w-8 h-12 bg-indigo-900 rounded shrink-0 border border-indigo-700 flex items-center justify-center text-[9px] font-bold text-indigo-300">
                      Buku 1
                    </div>
                    <div className="min-w-0">
                      <span className="text-[9px] sm:text-[10px] font-mono font-bold text-indigo-400 block truncate">
                        UIBN-979-xxxx-01-x
                      </span>
                      <h5 className="font-bold text-xs sm:text-sm text-white truncate">Karya Terbit 1</h5>
                      <span className="text-[10px] sm:text-xs text-slate-400 block">Kategori Resmi</span>
                    </div>
                  </div>

                  <div className="p-3 rounded-xl bg-slate-800/80 border border-slate-700/80 flex items-center gap-2.5">
                    <div className="w-8 h-12 bg-purple-900 rounded shrink-0 border border-purple-700 flex items-center justify-center text-[9px] font-bold text-purple-300">
                      Buku 2
                    </div>
                    <div className="min-w-0">
                      <span className="text-[9px] sm:text-[10px] font-mono font-bold text-indigo-400 block truncate">
                        UIBN-979-xxxx-02-x
                      </span>
                      <h5 className="font-bold text-xs sm:text-sm text-white truncate">Karya Terbit 2</h5>
                      <span className="text-[10px] sm:text-xs text-slate-400 block">Kategori Resmi</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mockup Share Footer */}
              <div className="pt-3 border-t border-slate-800 flex items-center justify-between text-xs sm:text-sm text-indigo-300">
                <span className="flex items-center gap-1.5 font-medium">
                  <Share2 className="w-4 h-4 text-indigo-400" />
                  <span>Siap Bagikan WhatsApp & QR Code</span>
                </span>
                <span className="text-[11px] sm:text-xs text-slate-400 font-mono">Status: Aktif</span>
              </div>
            </div>

            {/* Right: Key Benefits */}
            <div className="lg:col-span-6 space-y-3.5 2xl:space-y-4">
              <div className="p-4 2xl:p-5 rounded-2xl bg-slate-800/60 border border-slate-700/80 space-y-1.5">
                <div className="flex items-center gap-2 text-indigo-300 font-bold text-sm sm:text-base lg:text-lg 2xl:text-xl">
                  <ShieldCheck className="w-4 h-4 2xl:w-5 2xl:h-5 text-emerald-400 shrink-0" />
                  <span>1. Portofolio Digital Resmi Terautentikasi</span>
                </div>
                <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-300 leading-relaxed pl-6">
                  Menampilkan daftar judul buku terbit yang disetujui lengkap dengan stempel digital, barcode, dan sertifikat terverifikasi.
                </p>
              </div>

              <div className="p-4 2xl:p-5 rounded-2xl bg-slate-800/60 border border-slate-700/80 space-y-1.5">
                <div className="flex items-center gap-2 text-indigo-300 font-bold text-sm sm:text-base lg:text-lg 2xl:text-xl">
                  <Share2 className="w-4 h-4 2xl:w-5 2xl:h-5 text-indigo-400 shrink-0" />
                  <span>2. Promosi Praktis Lewat WhatsApp & QR Code</span>
                </div>
                <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-300 leading-relaxed pl-6">
                  Tersedia tombol bagikan WhatsApp otomatis dan QR Code khusus untuk dicetak pada flyer, banner, atau media sosial.
                </p>
              </div>

              <div className="p-4 2xl:p-5 rounded-2xl bg-slate-800/60 border border-slate-700/80 space-y-1.5">
                <div className="flex items-center gap-2 text-indigo-300 font-bold text-sm sm:text-base lg:text-lg 2xl:text-xl">
                  <Code className="w-4 h-4 2xl:w-5 2xl:h-5 text-sky-400 shrink-0" />
                  <span>3. Kode Widget Embed Website</span>
                </div>
                <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-300 leading-relaxed pl-6">
                  Dapatkan kode iFrame untuk menampilkan katalog terbitan secara langsung di website resmi atau blog pribadi.
                </p>
              </div>

              <div className="p-4 2xl:p-5 rounded-2xl bg-slate-800/60 border border-slate-700/80 space-y-1.5">
                <div className="flex items-center gap-2 text-indigo-300 font-bold text-sm sm:text-base lg:text-lg 2xl:text-xl">
                  <BookOpen className="w-4 h-4 2xl:w-5 2xl:h-5 text-amber-400 shrink-0" />
                  <span>4. Indeksasi Katalog Otomatis</span>
                </div>
                <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-300 leading-relaxed pl-6">
                  Seluruh karya terdaftar secara rapi dan mudah ditemukan oleh pembaca, pustakawan, serta akademisi.
                </p>
              </div>
            </div>
          </div>

          {/* Footer Registration Action */}
          <div className="pt-4 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-300 font-medium text-center sm:text-left">
              Daftarkan naskah buku Anda dan dapatkan halaman publik resmi UIBN.
            </p>

            {onOpenRegister && (
              <button
                type="button"
                onClick={onOpenRegister}
                className="px-5 sm:px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs sm:text-sm 2xl:text-base transition shadow-md shadow-indigo-600/30 flex items-center gap-2 cursor-pointer shrink-0"
              >
                <span>Daftar Akun Gratis</span>
                <ArrowRight className="w-4 h-4 2xl:w-5 2xl:h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
